import { AppShell, Box } from "@mantine/core";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/core/useAuth";
import { Sidebar } from "./Components/Sidebar";
import Header from "./Components/Header";
import LocationTracker from "../../shared/components/LocationTracker";
import { useState, createContext, useContext, useEffect } from "react";

export const SidebarContext = createContext();

export function MainLayout() {
  const { user, staffProfile, isLoading } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Add global styles for content containers - less aggressive
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* ScrollArea styling */
      .mantine-ScrollArea-scrollbar {
        background: transparent !important;
      }

      .mantine-ScrollArea-thumb {
        background: rgba(0, 0, 0, 0.1) !important;
        border-radius: 4px !important;
      }

      .mantine-ScrollArea-thumb:hover {
        background: rgba(0, 0, 0, 0.2) !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "1.2em",
        }}
      >
        Loading...
      </div>
    );
  }

  // Only redirect if definitely not authenticated
  if (!user || !staffProfile) {
    return <Navigate to="/auth" state={{ from: location }} replace={false} />;
  }

  return (
    <SidebarContext.Provider value={{ sidebarCollapsed, setSidebarCollapsed }}>
      <AppShell 
        padding="md" 
        navbar={{ width: sidebarCollapsed ? 75 : 240, breakpoint: "sm" }} 
        header={{ height: 55 }} 
        styles={{
          navbar: {
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          },
          main: {
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            paddingLeft: sidebarCollapsed ? "95px" : "260px",
            paddingRight: "20px",
            paddingTop: "75px",
            paddingBottom: "20px",
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            minHeight: "100vh",
          },
        }}
      >
        {/* Track location changes */}
        <LocationTracker />

        <AppShell.Header>
          <Header />
        </AppShell.Header>

        <AppShell.Navbar>
          <Sidebar />
        </AppShell.Navbar>

        <AppShell.Main>
          <Box
            style={{
              background: "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(10px)",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
              minHeight: "calc(100vh - 115px)",
              padding: "24px",
              width: "100%",
              boxSizing: "border-box",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Outlet />
          </Box>
        </AppShell.Main>
      </AppShell>
    </SidebarContext.Provider>
  );
}

export default MainLayout;
